import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { Property } from '@/lib/types';
import { ZONE_LABELS } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { Search, RefreshCw, Loader2 } from 'lucide-react';

export default function PropertiesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [regFilter, setRegFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (zoneFilter) params.zone = zoneFilter;
  if (regFilter) params.registrationType = regFilter;

  const { data: allProperties, loading, refresh } = useApi<Property[]>(
    () => api.getProperties(Object.keys(params).length > 0 ? params : undefined),
    [search, zoneFilter, regFilter]
  );

  async function handleSync() {
    setSyncing(true);
    try {
      const result = await api.syncProperties();
      await refresh();
      alert(`Sincronización completada.\nCreadas: ${result.created}\nActualizadas: ${result.updated}\nDesactivadas: ${result.deactivated}\nSin cambios: ${result.unchanged}${result.errors.length ? `\nErrores: ${result.errors.length}` : ''}`);
    } catch (err: any) {
      alert(`Error al sincronizar: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  }

  // Filter by service_pms (active in Hostify)
  const properties = allProperties?.filter(p => {
    if (showInactive) return true;
    const svc = p.hostifyData?.servicePms;
    // Show if no hostify data (manually created) or servicePms is 1/true
    return svc === undefined || svc === null || svc === 1;
  });

  return (
    <div>
      {/* En móvil el botón va debajo y a lo ancho: al lado del título partía el
          texto en dos líneas y dejaba un área de toque mínima. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold">Listings</h1>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center justify-center gap-1.5 w-full sm:w-auto px-3 py-2.5 sm:py-2 rounded-lg border text-sm text-sereno-dark/70 hover:bg-sereno-beau/20 disabled:opacity-50 transition-colors"
        >
          {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          <span className="sm:hidden">Sincronizar</span>
          <span className="hidden sm:inline">Sincronizar propiedades</span>
        </button>
      </div>

      <div className="card mb-6">
        {/* Apilado por defecto y en fila desde sm. Los `w-auto` de los dos
            desplegables los dejaban a media anchura y descuadrados en móvil;
            emparejados en una rejilla ocupan la fila entera y quedan alineados. */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input type="text" placeholder="Buscar..." className="input pl-9" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {/* Reparto desigual: "Todas las zonas" no cabe en media pantalla a
              16px —y bajar de ahí hace que iOS haga zoom al enfocar—, mientras
              que "VUT / AT" sobra con lo poco que le queda. */}
          <div className="grid grid-cols-[3fr_2fr] gap-3 sm:flex sm:gap-4">
            <select className="input w-full sm:w-auto" value={zoneFilter} onChange={e => setZoneFilter(e.target.value)}>
              <option value="">Todas las zonas</option>
              {Object.entries(ZONE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select className="input w-full sm:w-auto" value={regFilter} onChange={e => setRegFilter(e.target.value)}>
              <option value="">VUT / AT</option>
              <option value="VUT">VUT</option>
              <option value="AT">AT</option>
            </select>
          </div>
          <label className="flex items-center gap-2 py-1 text-sm text-sereno-dark/60 cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="rounded border-gray-300 w-4 h-4" />
            Mostrar inactivas
          </label>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : !properties || properties.length === 0 ? (
        <EmptyState
          title="No hay propiedades"
          description="Las propiedades se sincronizan automáticamente desde Hostify"
        />
      ) : (
        <div className="card overflow-hidden p-0">
          {/* Desktop table */}
          <table className="w-full hidden md:table">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Nickname</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Tipo</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Zona</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Licencia</th>
              </tr>
            </thead>
            <tbody>
              {properties.map(prop => (
                  <tr key={prop.id} onClick={() => navigate(`/properties/${prop.id}`)} className="border-b border-gray-50 hover:bg-sereno-blue/20 cursor-pointer transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-sm">{prop.nickname || prop.name}</p>
                      {prop.nickname && prop.name !== prop.nickname && <p className="text-xs text-sereno-dark/40">{prop.name}</p>}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`badge ${prop.registrationType === 'VUT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {prop.registrationType}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-sereno-dark/60">{ZONE_LABELS[prop.address?.zone] || prop.address?.zone || '-'}</td>
                    <td className="px-6 py-4">
                      {prop.touristLicense?.hasLicense ? (
                        <span className="badge bg-green-100 text-green-700">Sí</span>
                      ) : (
                        <span className="badge bg-gray-100 text-gray-500">No</span>
                      )}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
          {/* Tarjetas en móvil. Llevan la zona y el estado de la licencia
              —también cuando falta— porque son dos de las cuatro columnas de la
              tabla: sin ellas, en el móvil no se puede saber lo mismo. */}
          <div className="md:hidden divide-y divide-gray-50">
            {properties.map(prop => (
                <div key={prop.id} onClick={() => navigate(`/properties/${prop.id}`)} className="px-4 py-3.5 hover:bg-sereno-blue/20 cursor-pointer transition-colors active:bg-sereno-blue/30">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{prop.nickname || prop.name}</p>
                      {prop.nickname && prop.name !== prop.nickname && <p className="text-xs text-sereno-dark/40">{prop.name}</p>}
                    </div>
                    <span className={`badge flex-shrink-0 ${prop.registrationType === 'VUT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {prop.registrationType}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="text-xs text-sereno-dark/60">
                      {ZONE_LABELS[prop.address?.zone] || prop.address?.zone || 'Sin zona'}
                    </span>
                    <span className="text-sereno-dark/20">·</span>
                    {prop.touristLicense?.hasLicense ? (
                      <span className="badge bg-green-100 text-green-700 text-xs">Con licencia</span>
                    ) : (
                      <span className="badge bg-gray-100 text-gray-500 text-xs">Sin licencia</span>
                    )}
                  </div>
                </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
